<template>
  <br />
  <div class="col-sm-12">
    <div class="card">
      <div class="card-header">
        <h3>{{ tableTitle }}</h3>
      </div>
      <div class="card-body">
        <form @submit.prevent="saveData">
          <div v-for="(col, index) in tableColumnsLocal" :key="index" class="mb-3">
            <label :for="'field-' + index" class="form-label">{{ friendlyLabel(col) }}</label>
            <template v-if="isBooleanLike(tableData[0] ? tableData[0][col] : null)">
              <input
                :id="'field-' + index"
                type="checkbox"
                class="form-check-input"
                :checked="toBoolean(tableData[0] ? tableData[0][col] : false)"
                @change="onBooleanChange($event.target.checked, col)"
                :disabled="isReadonly(col)"
              />
            </template>
            <template v-else>
              <input
                :value="displayValue(tableData[0] ? tableData[0][col] : null, col)"
                @input="onInput($event.target.value, col)"
                :id="'field-' + index"
                type="text"
                class="form-control"
                :readonly="isReadonly(col)"
                :disabled="isReadonly(col)"
              />
            </template>
          </div>
        </form>
      </div>
      <div class="card-footer">
        <div class="d-flex justify-content-between align-items-center mt-2">
          <button class="btn btn-primary ms-auto" @click="saveData">Guardar</button>
          <div v-if="loading" class="spinner-border spinner-border-sm" role="status">
            <span class="visually-hidden">Cargando...</span>
          </div>
          <router-link :to="backToListRoute" class="btn btn-secondary ms-auto">Regresar</router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/axios';
import { extractItems } from '@/utils/listResponse';
import { useRouteStore } from '@/stores/routeStore';
import { useBootsrapStore } from '@/stores/bootsrap';

export default {
  name: 'catalogoDetailView',
  props: {
    tableTitle: { type: String, default: 'Detalle' },
    tableName: { type: String, default: '' },
    // optional array of column keys; if empty the component will derive from fetched data
    tableColumns: { type: Array, default: () => [] },
    // id of the record to fetch; if not provided, will use $route.params.id
    recordId: { type: [String, Number], default: null },
    readonlyFields: { type: Array, default: () => ['Id','created', 'updated'] },
    labels: { type: Object, default: () => ({'Id': '#', 'IsActive': 'Activo', 'Created': 'Creado', 'Updated': 'Actualizado'}) },
    // optional route to navigate back to the list view (e.g. '/geograficos/ciudades')
    backRoute: { type: String, default: null },
  },

  data() {
    return {
      tableColumnsLocal: Array.isArray(this.tableColumns) && this.tableColumns.length ? this.tableColumns.slice() : [],
      tableData: [],
      loading: false,
      error: null,
      currentTableName: this.tableName || null,
      currentRecordId: this.recordId != null ? this.recordId : null,
    };
  },

  computed: {
    bootstraplist() {
      const s = useBootsrapStore();
      return s.bootstrap || [];
    },
    backToListRoute() {
      // if a specific back route was provided prefer it
      if (this.backRoute) return this.backRoute;
      // default back route derives from tableName if present
      if (this.currentTableName) return `/${this.currentTableName}`;
      return this.$route && this.$route.query && this.$route.query.back ? this.$route.query.back : '/';
    }
  },

  created() {
    const routeStore = useRouteStore();
    const idParam = this.currentRecordId != null ? this.currentRecordId : (this.$route && this.$route.params ? this.$route.params.id : null);
    routeStore.setId(idParam != null ? idParam : 0);
    this.currentRecordId = idParam;

    if (!this.currentTableName && this.$route && this.$route.query && this.$route.query.tableName) {
      this.currentTableName = this.$route.query.tableName;
    }

    if (typeof this.loadData === 'function') this.loadData();
  },

  watch: {
    '$route.params.id'(newId) {
      const routeStore = useRouteStore();
      routeStore.setId(newId != null ? newId : 0);
      this.currentRecordId = newId;
      this.loadData();
    }
  },

  methods: {
    async fetchData() {
      try {
        this.loading = true;
        this.error = null;
        if (!this.currentTableName) {
          this.error = 'Tabla no especificada.';
          return null;
        }

        // if we have an id, try to fetch single record
        if (this.currentRecordId != null) {
          const id = this.currentRecordId;
          // try with and without trailing slash
          try {
            const res = await api.get(`${this.currentTableName}/${id}/`);
            return res.data;
          } catch (e) {
            const res2 = await api.get(`${this.currentTableName}/${id}`);
            return res2.data;
          }
        }

        // fallback: fetch collection
        const response = await api.get(this.currentTableName);
        return extractItems(response.data);
      } catch (err) {
        console.error('Error fetching data:', err);
        this.error = 'No se pudieron cargar los datos.';
        return null;
      } finally {
        this.loading = false;
      }
    },

    async loadData() {
      const data = await this.fetchData();
      if (data == null) {
        this.tableData = [];
        return;
      }

      // normalize to array so template can use [0]
      if (Array.isArray(data)) this.tableData = data;
      else if (typeof data === 'object') this.tableData = [data];
      else this.tableData = [];

      if (!this.tableColumnsLocal || this.tableColumnsLocal.length === 0) {
        if (this.tableData.length > 0) this.tableColumnsLocal = Object.keys(this.tableData[0]);
      }
    },

    async saveData() {
      try {
        this.loading = true;
        this.error = null;
        if (this.tableData.length === 0) {
          this.error = 'No hay datos para guardar.';
          return;
        }
        const id = this.tableData[0].id || this.currentRecordId;
        if (!id) {
          this.error = 'Id del registro no disponible.';
          return;
        }
        await api.put(`${this.currentTableName}/${id}/`, this.tableData[0]);
        this.$emit('saved', { id });
        this.changeSaveButtonText('Guardando...');
      } catch (err) {
        console.error('Error saving data:', err);
        this.error = 'No se pudieron guardar los datos.';
      } finally {
        this.loading = false;
      }
    },

    changeSaveButtonText(newText) {
      const saveButton = this.$el.querySelector('button.btn-primary');
      if (saveButton) {
        const originalText = saveButton.textContent;
        saveButton.textContent = newText;
        setTimeout(() => {
          saveButton.textContent = originalText;
        }, 2000);
      }
    },

    friendlyLabel(key) {
      if (!key) return '';
      if (this.labels && Object.prototype.hasOwnProperty.call(this.labels, key)) return this.labels[key];
      const snakeToSpace = key.replace(/_/g, ' ');
      const title = snakeToSpace.replace(/\b\w/g, l => l.toUpperCase());
      if (this.labels && Object.prototype.hasOwnProperty.call(this.labels, title)) return this.labels[title];
      const lower = key.toLowerCase();
      if (this.labels && Object.prototype.hasOwnProperty.call(this.labels, lower)) return this.labels[lower];
      return title;
    },

    isReadonly(key) {
      if (!key) return false;
      if (!Array.isArray(this.readonlyFields)) return false;
      const normalize = s => String(s).toLowerCase().replace(/[_\s]/g, '');
      const nk = normalize(key);
      return this.readonlyFields.some(f => normalize(f) === nk);
    },

    isIsoDateString(val) {
      if (typeof val !== 'string') return false;
      const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z?$/;
      const isoNoT = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
      return isoRegex.test(val) || isoNoT.test(val);
    },

    isDateField(key, value) {
      if (!key && value == null) return false;
      const kd = (key || '').toLowerCase();
      if (/(date|fecha|time|hora|created|updated)/i.test(kd)) return true;
      if (typeof value === 'string' && this.isIsoDateString(value)) return true;
      if (typeof value === 'number') return value > 1000000000;
      return false;
    },

    isBooleanLike(val) {
      if (typeof val === 'boolean') return true;
      if (typeof val === 'string') {
        const v = val.trim().toLowerCase();
        return v === 'true' || v === 'false' || v === '1' || v === '0';
      }
      if (typeof val === 'number') return val === 0 || val === 1;
      return false;
    },

    toBoolean(val) {
      if (typeof val === 'boolean') return val;
      if (typeof val === 'string') {
        const v = val.trim().toLowerCase();
        return v === 'true' || v === '1';
      }
      if (typeof val === 'number') return val === 1;
      return Boolean(val);
    },

    onBooleanChange(checked, key) {
      if (!this.tableData || this.tableData.length === 0) this.tableData = [{}];
      this.tableData[0][key] = checked;
    },

    formatDateTime(val) {
      if (val == null || val === '') return '';
      let d = null;
      if (val instanceof Date) d = val;
      else if (typeof val === 'number') d = new Date(val);
      else if (this.isIsoDateString(String(val))) d = new Date(val);
      else {
        const parsed = Date.parse(val);
        if (!isNaN(parsed)) d = new Date(parsed);
      }
      if (!d || isNaN(d.getTime())) return String(val);
      const pad = n => String(n).padStart(2, '0');
      const day = pad(d.getDate());
      const month = pad(d.getMonth() + 1);
      const year = String(d.getFullYear());
      const hours = pad(d.getHours());
      const minutes = pad(d.getMinutes());
      const seconds = pad(d.getSeconds());
      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    },

    parseDisplayDate(display) {
      if (!display || typeof display !== 'string') return display;
      const parts = display.trim().split(' ');
      const dateParts = parts[0].split('/');
      if (dateParts.length !== 3) return display;
      const day = parseInt(dateParts[0], 10);
      const month = parseInt(dateParts[1], 10) - 1;
      const year = parseInt(dateParts[2], 10);
      let hours = 0, minutes = 0, seconds = 0;
      if (parts[1]) {
        const timeParts = parts[1].split(':');
        hours = parseInt(timeParts[0] || '0', 10);
        minutes = parseInt(timeParts[1] || '0', 10);
        seconds = parseInt(timeParts[2] || '0', 10);
      }
      const d = new Date(year, month, day, hours, minutes, seconds);
      if (isNaN(d.getTime())) return display;
      return d.toISOString();
    },

    displayValue(value, key) {
      if (this.isDateField(key, value)) return this.formatDateTime(value);
      if (value == null) return '';
      return String(value);
    },

    onInput(inputValue, key) {
      if (!this.tableData || this.tableData.length === 0) this.tableData = [{}];
      const idx = 0;
      if (this.isDateField(key, this.tableData[idx][key] || inputValue)) {
        const parsed = this.parseDisplayDate(inputValue);
        this.tableData[idx][key] = parsed;
      } else {
        this.tableData[idx][key] = inputValue;
      }
    },
  },
};
</script>
